import { MAX_CAPTURE_ATTEMPTS } from '@lib/media/frameCaptureCore';
import { peekVideoFrame } from '@lib/media/hlsFrameCapture';
import { useMomentThumbnailStore } from '@stores/MomentThumbnail/MomentThumbnailStore';
import { useEffect, useRef, useState } from 'react';

type UseMomentThumbnailOptions = {
  /** Skip capture entirely — e.g. audio-only media, which has no frame to grab. */
  enabled?: boolean;
  /**
   * Cap on the captured frame's width. Pass the size it will actually be shown at,
   * with hidpi headroom.
   *
   * Without it the capture falls back to the module's 1280px ceiling, so a
   * thumbnail displayed 320px wide asks for four times the pixels it can use. That
   * matters beyond the wasted decode: these are the largest entries in a
   * byte-bounded cache shared with the filmstrip, and their consumers re-read them
   * rarely, so they are simultaneously the first frames evicted and the most
   * expensive to capture again — each on a different media, forcing a full player
   * rebuild.
   */
  maxWidth?: number;
};

export const useMomentThumbnail = (
  libraryId: string,
  mediaId: string,
  timestamp: number,
  { enabled = true, maxWidth }: UseMomentThumbnailOptions = {},
) => {
  const store = useMomentThumbnailStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const attemptsRef = useRef(0);

  // The cache is the only source of truth for the URL — deliberately not React
  // state.
  //
  // The frame is a Blob behind an object URL that the capture module owns and
  // revokes when it evicts. Keeping our own copy would mean handing out a dead
  // link the moment that happened, and we could not even tell: a URL in state
  // with nothing in the cache means exactly "evicted", because the capture
  // resolves only after the cache is written. Reading through instead makes a
  // miss the signal to capture again, so first load and post-eviction reload are
  // one path rather than two, and there is never a stale URL to hand back.
  //
  // The options here must match the request below exactly: size is part of a
  // frame's cache identity, so a mismatch would read a key nothing ever writes.
  const thumbnail = enabled ? peekVideoFrame(libraryId, mediaId, timestamp, { maxWidth }) : null;

  // A different frame is a fresh subject: clear the error and the attempt budget.
  useEffect(() => {
    attemptsRef.current = 0;
    setError(null);
  }, [libraryId, mediaId, timestamp, maxWidth]);

  useEffect(() => {
    if (!enabled || thumbnail) return;
    if (attemptsRef.current >= MAX_CAPTURE_ATTEMPTS) return;

    // No in-flight guard. There is nothing to guard against — nothing this effect
    // sets appears in its own deps, so it cannot re-enter for a given frame — and
    // a guard that outlived a single run would block the one case that matters:
    // when a dep changes mid-capture (trimming a clip moves `timestamp` on a live
    // instance), the cleanup aborts, and a stale "still in flight" flag would make
    // the re-run bail without ever requesting the new frame.
    attemptsRef.current += 1;
    const controller = new AbortController();
    const { signal } = controller;

    void (async () => {
      setIsLoading(true);
      setError(null);
      try {
        await store.getThumbnail(libraryId, mediaId, timestamp, { maxWidth }, signal);
        // A success re-arms the budget. The cap is here to stop a frame that is
        // captured and then instantly evicted from asking forever — it counts
        // consecutive failures, not attempts over the component's lifetime. Without
        // this reset, three legitimate evictions across a long session would retire
        // the frame permanently: no error, no spinner, just a blank cell.
        if (!signal.aborted) attemptsRef.current = 0;
      } catch (err) {
        if (!signal.aborted) {
          const isAbort = err instanceof Error && err.name === 'AbortError';
          if (!isAbort) {
            setError(err instanceof Error ? err.message : 'Failed to generate thumbnail');
          }
        }
      } finally {
        // Unconditionally, including when aborted. `MomentThumbnail` renders its
        // spinner before it looks at the frame, so leaving this set on an aborted
        // capture is a spinner that never resolves. A superseding run sets it back
        // to true immediately, and on unmount the write is a no-op.
        setIsLoading(false);
      }
    })();

    return () => {
      controller.abort();
    };
  }, [enabled, thumbnail, libraryId, mediaId, timestamp, maxWidth, store]);

  return { thumbnail, isLoading, error };
};
